import { useMemo } from 'react'
import { Button, type Option, SelectMultipleV2 } from '~/components/ui'
import { usePlaceSearch } from '~/hooks'
import {
  formatPlaceLabel,
  toCanonicalPlaceLabelKey,
  toCanonicalPlaceSearchText,
} from '~/utils/addressSearch'

type SearchType = 'city' | 'county' | 'postalCode'
type FilterKey = 'cities' | 'counties' | 'postalCodes'

type MapPlaceAutocompleteFilterProps = {
  label: string
  searchType: SearchType
  placeholder: string
  selectedValues: string[]
  allowedStates?: string[]
  filterKey: FilterKey
  updateFilter: (updates: Record<string, any>) => void
}

function toFilterValue(
  value: string,
  searchType: SearchType,
  stateAbbr = ''
): string {
  const trimmed = value.trim()

  if (searchType === 'postalCode') {
    return trimmed.replace(/\D/g, '').slice(0, 5)
  }

  if (searchType === 'county') {
    const county = trimmed
      .replace(/county/gi, '')
      .replace(/\s+/g, ' ')
      .trim()

    if (
      stateAbbr.trim().toUpperCase() === 'LA' &&
      county &&
      !/\bparish$/i.test(county)
    ) {
      return `${county} Parish`
    }

    return county
  }

  return trimmed
}

function getOptionLabel(
  prediction: google.maps.places.AutocompletePrediction,
  searchType: SearchType
): string {
  if (searchType === 'postalCode') {
    return prediction.structured_formatting.main_text
  }

  return formatPlaceLabel(
    prediction.structured_formatting.main_text || prediction.description
  )
}

function getStateAbbreviation(
  prediction: google.maps.places.AutocompletePrediction
): string {
  const sources = [
    prediction.structured_formatting.secondary_text,
    prediction.description,
  ].filter(Boolean)

  for (const source of sources) {
    const terms = source
      .toUpperCase()
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)

    for (const term of terms) {
      const stateMatch = term.match(/^([A-Z]{2})(?:\b|$)/)
      if (stateMatch?.[1]) {
        return stateMatch[1]
      }
    }
  }

  return ''
}

function getDisplayLabel(
  prediction: google.maps.places.AutocompletePrediction,
  searchType: SearchType,
  value: string
): string {
  if (searchType === 'postalCode') {
    return value
  }

  const baseLabel = getOptionLabel(prediction, searchType)
  const stateAbbr = getStateAbbreviation(prediction)
  if (!stateAbbr) {
    return searchType === 'county' ? value : baseLabel
  }

  const label = searchType === 'county' ? value : baseLabel

  return `${label}, ${stateAbbr}`
}

function getPredictionDedupKey(
  prediction: google.maps.places.AutocompletePrediction,
  searchType: SearchType,
  value: string
): string {
  if (searchType === 'postalCode') {
    return value
  }

  const stateAbbr = getStateAbbreviation(prediction)
  const canonicalValue = toCanonicalPlaceLabelKey(value).toUpperCase()

  return `${canonicalValue}|${stateAbbr}`
}

function getOptionInternalValue(
  prediction: google.maps.places.AutocompletePrediction,
  searchType: SearchType,
  value: string
): string {
  if (searchType === 'postalCode') {
    return value
  }

  return prediction.place_id
}

function getOptionFilterValue(option: Option, searchType: SearchType): string {
  const rawValue =
    typeof option.rawValue === 'string' ? option.rawValue : option.value
  return toFilterValue(rawValue, searchType)
}

export default function MapPlaceAutocompleteFilter({
  label,
  searchType,
  placeholder,
  selectedValues,
  allowedStates = [],
  filterKey,
  updateFilter,
}: MapPlaceAutocompleteFilterProps) {
  const { setSearch, predictions, setPredictions } = usePlaceSearch(searchType)
  const allowedStateSet = useMemo(
    () =>
      new Set(
        allowedStates.map((state) => state.trim().toUpperCase()).filter(Boolean)
      ),
    [allowedStates]
  )
  const selectedValueSet = useMemo(
    () =>
      new Set(
        selectedValues
          .map((value) => {
            const filteredValue = toFilterValue(value, searchType)

            return searchType === 'postalCode'
              ? filteredValue
              : toCanonicalPlaceLabelKey(filteredValue)
          })
          .filter(Boolean)
      ),
    [selectedValues, searchType]
  )

  const options: Option[] = useMemo(() => {
    const seen = new Set<string>()

    return predictions.reduce<Option[]>((acc, prediction) => {
      const stateAbbr = getStateAbbreviation(prediction)
      if (
        allowedStateSet.size > 0 &&
        (!stateAbbr || !allowedStateSet.has(stateAbbr))
      ) {
        return acc
      }

      const rawLabel = getOptionLabel(prediction, searchType)
      const value = toFilterValue(rawLabel, searchType, stateAbbr)
      const displayLabel = getDisplayLabel(prediction, searchType, value)
      const selectedValueKey =
        searchType === 'postalCode' ? value : toCanonicalPlaceLabelKey(value)
      const dedupeKey = getPredictionDedupKey(prediction, searchType, value)

      if (
        !value ||
        selectedValueSet.has(selectedValueKey) ||
        seen.has(dedupeKey)
      ) {
        return acc
      }

      seen.add(dedupeKey)
      acc.push({
        value: getOptionInternalValue(prediction, searchType, value),
        label: displayLabel,
        rawValue: value,
      })

      return acc
    }, [])
  }, [predictions, searchType, allowedStateSet, selectedValueSet])

  const selectedOptions = useMemo<Option[]>(
    () =>
      selectedValues.map((value) => ({
        value,
        label: value,
      })),
    [selectedValues]
  )

  return (
    <div className="space-y-2">
      <p className="text-sm">
        {label}
        {selectedValues.length > 0 && (
          <Button
            variant="link"
            size="none"
            className="ml-2"
            onClick={() => {
              setSearch('')
              setPredictions([])
              updateFilter({ [filterKey]: undefined })
            }}>
            (reset)
          </Button>
        )}
      </p>

      <SelectMultipleV2
        isPopover={true}
        placeholder={placeholder}
        options={options}
        value={selectedOptions}
        emptyIndicator={null}
        commandProps={{
          filter: (value, search) => {
            if (!search.trim()) {
              return 1
            }

            return toCanonicalPlaceSearchText(value).includes(
              toCanonicalPlaceSearchText(search)
            )
              ? 1
              : 0
          },
        }}
        onChange={(selected) => {
          const values = Array.from(
            new Set(
              selected
                .map((option) => getOptionFilterValue(option, searchType))
                .filter(Boolean)
            )
          )
          const isAdding = values.length > selectedValues.length
          if (isAdding) {
            setSearch('')
            setPredictions([])
          }

          updateFilter({ [filterKey]: values.length > 0 ? values : undefined })
        }}
        inputProps={{
          onValueChange: (value) => {
            setSearch(value)
          },
          onBlur: () => {
            setSearch('')
            setPredictions([])
          },
        }}
      />
    </div>
  )
}
